import React from 'react';
import { X, Download, ExternalLink, AlertCircle } from 'lucide-react';
import PdfViewerWithFallback from './PdfViewerWithFallback';
import './FilePreviewModal.css';

/**
 * Simplified PDF Preview Modal - Uses most basic approach
 * This version strips away all complexity to focus on getting PDFs to display
 */
const FilePreviewModalSimple = ({ file, isOpen, onClose }) => {
  if (!isOpen || !file) return null;

  // Get the file URL
  const getFileUrl = () => {
    // Try all possible URL fields - INCLUDING the simple 'url' field from storage service
    const url = file.url ||  // Added this - the field returned by uploadFile in supabaseStorageService
                file.public_url || 
                file.publicUrl || 
                file.direct_download_url || 
                file.signed_url ||
                file.signedUrl ||
                (file.file_path ? `https://db.ab-civil.com/storage/v1/object/public/project-files/${file.file_path}` : null);
    
    console.log('FilePreviewModalSimple - Resolved URL:', url);
    console.log('FilePreviewModalSimple - File object:', file);
    return url;
  };

  const fileUrl = getFileUrl();
  const fileName = file.filename || file.original_filename || file.originalFilename || file.fileName || 'Document.pdf';
  const fileSize = file.size || file.file_size || file.fileSize;
  const fileType = file.type || file.file_type || file.fileType || 'application/pdf';
  
  // Check file type
  const isPDF = fileType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
  const isImage = fileType.startsWith('image/') || fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  return (
    <div className="file-preview-overlay" onClick={handleOverlayClick}>
      <div className="file-preview-modal" style={{ maxWidth: '90vw', maxHeight: '90vh' }}>
        {/* Header */}
        <div className="preview-header">
          <div className="preview-title">
            <h3>{fileName}</h3>
            {fileSize && (
              <span className="file-metadata" style={{ fontSize: '14px', color: '#6b7280' }}>
                {formatFileSize(fileSize)}
              </span>
            )}
          </div>
          
          <div className="preview-actions">
            
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="action-btn"
              title="Open in new tab"
              style={{ padding: '8px', textDecoration: 'none' }}
            >
              <ExternalLink size={20} />
            </a>
            
            <a
              href={fileUrl}
              download={fileName}
              className="action-btn"
              title="Download"
              style={{ padding: '8px', textDecoration: 'none' }}
            >
              <Download size={20} />
            </a>
            
            <button 
              className="action-btn"
              onClick={onClose}
              title="Close"
              style={{ padding: '8px' }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="preview-content" style={{ height: 'calc(100% - 80px)', overflow: 'hidden' }}>
          {!fileUrl ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <AlertCircle size={48} style={{ color: '#ef4444', marginBottom: '16px' }} />
              <p>No file URL available</p>
            </div>
          ) : (
            <>
              {/* Image Preview */}
              {isImage && (
                <div style={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  padding: '20px'
                }}>
                  <img
                    src={fileUrl}
                    alt={fileName}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '100%', 
                      objectFit: 'contain' 
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div style="text-align: center; padding: 40px;">
                          <p style="color: #6b7280;">Unable to load image</p>
                          <a href="${fileUrl}" target="_blank" style="color: #3b82f6;">Open in new tab</a>
                        </div>
                      `;
                    }}
                  />
                </div>
              )}

              {/* PDF Preview - Use the new component with fallbacks */}
              {isPDF && (
                <PdfViewerWithFallback 
                  fileUrl={fileUrl} 
                  fileName={fileName} 
                />
              )}

              {/* Other file types */}
              {!isImage && !isPDF && (
                <div style={{ 
                  padding: '60px', 
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <ExternalLink size={64} style={{ color: '#6b7280', marginBottom: '24px' }} />
                  <h3 style={{ marginBottom: '16px' }}>Preview Not Available</h3>
                  <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                    This file type cannot be previewed directly.
                  </p>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <ExternalLink size={20} />
                      Open in New Tab
                    </a>
                    <a
                      href={fileUrl}
                      download={fileName}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Download size={20} />
                      Download
                    </a>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModalSimple;